import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, MessageSquare, Wallet, Sparkles, Rocket, HelpCircle } from "lucide-react";
import { getStats } from "../lib/api";
import { formatNumber, formatEUR } from "../lib/constants";
import HowItWorks from "../components/HowItWorks";
import TrustBar from "../components/TrustBar";
import FAQAccordion from "../components/FAQAccordion";
import { HOMEPAGE_FAQ } from "../lib/faqData";
import usePageMeta from "../lib/usePageMeta";

export default function Landing() {
  usePageMeta({
    title: "CheatCraft — The Marketplace for Cheat Providers & Media Creators",
    description:
      "Stop getting scammed. CheatCraft connects cheat software providers with trusted media creators. Escrow payments, watermark protection, secure file sharing. Find your creator today.",
    ogTitle: "CheatCraft — Cheats Meet Creators",
    ogDescription:
      "The secure marketplace connecting cheat providers with media creators. Escrow payments, watermark protection, guaranteed delivery.",
    canonicalPath: "/",
  });
  const [stats, setStats] = useState(null);
  useEffect(() => { getStats().then(setStats).catch(() => {}); }, []);

  return (
    <div data-testid="landing-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 cc-grid-bg opacity-40 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(0,255,136,0.14), transparent 60%)" }} />
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-24 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#15152a] bg-[#0a0a14] mb-6 cc-anim-up">
            <span className="cc-status-dot online cc-pulse" />
            <span className="text-[11px] uppercase tracking-widest text-[#a0a0b0]">Live marketplace · v0.1</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05] text-white max-w-4xl cc-anim-up">
            The marketplace that connects <span className="text-[#00ff88]">cheats companies</span> with <span className="text-[#00bbff]">media creators</span>.
          </h1>
          <p className="text-base md:text-lg text-[#a0a0b0] mt-6 max-w-2xl cc-anim-up" style={{ animationDelay: "60ms" }}>
            Tired of getting scammed on Discord? CheatCraft connects cheat providers with media creators through a secure escrow system. No trust needed — just results.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-10 cc-anim-up" style={{ animationDelay: "120ms" }}>
            <Link
              to="/signup/company"
              data-testid="cta-company"
              className="cc-btn-primary rounded-xl px-6 py-3.5 inline-flex items-center gap-2"
            >
              I'm a Company <ArrowRight size={16} />
            </Link>
            <Link
              to="/signup/creator"
              data-testid="cta-creator"
              className="rounded-xl px-6 py-3.5 border border-[#15152a] text-white bg-[#0a0a14] hover:border-[#1f1f3a] transition-colors inline-flex items-center gap-2"
            >
              I'm a Creator <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-16 cc-anim-up" style={{ animationDelay: "180ms" }}>
            <StatBox label="Creators" value={formatNumber(stats?.creators ?? 0)} />
            <StatBox label="Companies" value={formatNumber(stats?.companies ?? 0)} />
            <StatBox label="Orders Delivered" value={formatNumber(stats?.orders_delivered ?? 0)} />
            <StatBox label="Volume" value={formatEUR(stats?.volume_eur ?? 0)} accent />
          </div>
        </div>
      </section>

      {/* Interactive How It Works */}
      <HowItWorks />

      <TrustBar />

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl">
          <div className="cc-overline">Why CHEATCRAFT</div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-3">
            One platform. End-to-end.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          <ValueCard
            icon={Search}
            title="Find Creators"
            desc="Filter by game, video quality, language, content type. 1,200+ verified creators."
          />
          <ValueCard
            icon={MessageSquare}
            title="Manage Orders"
            desc="Brief, deadline, revisions, delivery — transparent tracking at every step."
          />
          <ValueCard
            icon={Wallet}
            title="Escrow Payments"
            desc="Funds held securely. Released only after the company approves the delivery."
          />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="cc-overline">Roadmap</div>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-3 mb-10">
          Coming soon.
        </h2>
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl">
          <SoonCard icon={Rocket} title="Developer Marketplace" desc="Freelance dev for gaming studios." />
          <SoonCard icon={Sparkles} title="Job Board" desc="Studios post jobs, creators & devs apply." />
        </div>
      </section>

      <section data-testid="landing-faq" className="max-w-3xl mx-auto px-6 py-24">
        <div className="text-center mb-10">
          <div className="cc-overline inline-flex items-center gap-2"><HelpCircle size={11} /> FAQ</div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-3">
            Quick answers to <span className="text-[#00ff88]">common questions</span>.
          </h2>
          <p className="text-sm text-[#a0a0b0] mt-3 max-w-xl mx-auto">
            Six things people ask us most. Got more? Open the full help center.
          </p>
        </div>

        <FAQAccordion items={HOMEPAGE_FAQ} testIdPrefix="home-faq" />

        <div className="text-center mt-8">
          <Link
            to="/faq"
            data-testid="home-faq-more"
            className="inline-flex items-center gap-2 text-sm text-[#00ff88] hover:underline"
          >
            More questions? Visit our full FAQ <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}

const StatBox = ({ label, value, accent }) => (
  <div data-testid={`stat-${label}`} className="cc-surface rounded-xl p-4">
    <div className="cc-overline">{label}</div>
    <div className={`font-mono text-2xl mt-1 ${accent ? "text-[#00ff88]" : "text-white"}`}>{value}</div>
  </div>
);

const ValueCard = ({ icon: Icon, title, desc }) => (
  <div className="cc-surface cc-card-hover rounded-2xl p-7">
    <div className="w-10 h-10 rounded-lg border border-[#15152a] bg-[#06060c] flex items-center justify-center text-[#00ff88] mb-5">
      <Icon size={18} />
    </div>
    <h3 className="text-lg font-semibold text-white">{title}</h3>
    <p className="text-sm text-[#a0a0b0] leading-relaxed mt-2">{desc}</p>
  </div>
);

const StepCard = ({ n, title, desc }) => (
  <div className="cc-surface rounded-2xl p-7 relative overflow-hidden cc-card-hover">
    <div className="absolute top-4 right-5 font-mono text-5xl text-[#0d0d1a]">{n}</div>
    <div className="cc-overline relative">Step {n}</div>
    <h3 className="text-xl font-semibold text-white mt-2 relative">{title}</h3>
    <p className="text-sm text-[#a0a0b0] mt-2 relative">{desc}</p>
  </div>
);

const SoonCard = ({ icon: Icon, title, desc }) => (
  <div className="cc-surface rounded-2xl p-5 relative">
    <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-[#ffaa00] bg-[#ffaa00]/10 border border-[#ffaa00]/20 rounded-full px-2 py-0.5">
      Soon
    </span>
    <div className="w-9 h-9 rounded-lg border border-[#15152a] bg-[#06060c] flex items-center justify-center text-[#00bbff]">
      <Icon size={16} />
    </div>
    <div className="text-white font-semibold mt-4">{title}</div>
    <div className="text-xs text-[#a0a0b0] mt-1">{desc}</div>
  </div>
);
